import { createSemanticInterpreter } from '../ai/adapter'
import type {
  Interpretation,
  InterpretationRequest,
  SemanticInterpreter,
} from '../ai/types'
import {
  canonicalStateDigest,
  canonicalizeTraits,
  composeMetaphor,
  createInitialGameState,
  deriveInheritedTraits,
  gameReducer,
  getCurrentScenario,
  INITIAL_SELECTION,
  isSelectionComplete,
  normalizeMetaphorInput,
  SCENARIOS,
  TRAITS,
  TRAIT_LABELS,
  type AnomalyScenario,
  type GameState,
  type MetaphorEvaluation,
  type Trait,
} from '../domain'
import { createGame } from '../game/createGame'
import { emitFx } from '../game/fxBridge'
import {
  anchorConfirmedTraits,
  type DisplayExcerptAnchor,
} from './presentationAnchors'
import { showOnboarding } from './onboarding'

const SCAN_STEPS = [
  '문장을 확인했습니다.',
  '문장에서 성질을 읽는 중…',
  '표현 사이의 관계를 다시 묶는 중…',
  '이번 목표와 대조하는 중…',
] as const

const TRAIT_CLUES: Readonly<Record<Trait, string>> = {
  bounded: '머물 마지막 지점',
  gentle: '상처를 남기지 않는 움직임',
  named: '붙잡아 부를 호칭',
  cyclic: '처음으로 되돌아오는 궤적',
  timed: '대답 뒤 닫히는 순간',
  remembering: '받은 것을 잊지 않는 흔적',
  reciprocal: '오간 만큼 되돌아오는 관계',
  reflective: '소유하지 않는 반대편',
  released: '붙든 것을 보내는 결말',
  forceful: '저항을 지우는 힘',
  endless: '마지막을 없애는 반복',
  hungry: '다음을 요구하는 결핍',
  forgetful: '받은 것을 지우는 망각',
}

const GOAL_LINES: Readonly<Record<string, string>> = {
  'unsewn-thread': '끝과 이름이 있고, 힘으로 밀어붙이지 않는 실을 만드세요.',
  'aftertone-bell': '기억한 것을 한 번 돌려주고, 정해진 때에 끝나는 종을 만드세요.',
  'borrowed-mirror': '앞선 의미를 비추고, 붙든 것을 모두 놓아주는 거울을 만드세요.',
}

const REPAIR_HINTS: Readonly<Record<Trait, string>> = {
  bounded: '결말에 멈출 장소나 마지막 지점을 만들어 보세요.',
  gentle: '움직임이 밀어붙이지 않고 스스로 낮아지게 바꿔 보세요.',
  named: '대상을 붙잡아 부를 한정된 이름을 넣어 보세요.',
  cyclic: '움직임이 처음으로 돌아오는 관계를 만들어 보세요.',
  timed: '횟수나 응답 뒤에 분명한 종료 시점을 놓아 보세요.',
  remembering: '받은 이름이나 약속을 보존하는 흔적을 남겨 보세요.',
  reciprocal: '받기만 하지 않고 같은 것을 돌려주는 동사를 써 보세요.',
  reflective: '소유하지 않고 방향만 바꾸어 보여 주게 해 보세요.',
  released: '마지막에 붙든 대상을 보내는 행동을 넣어 보세요.',
  forceful: '베기·밀기·지우기보다 저항을 허용하는 동작으로 바꿔 보세요.',
  endless: '계속됨을 끊을 횟수나 마지막 지점을 넣어 보세요.',
  hungry: '다음을 요구하거나 삼키는 표현을 걷어내 보세요.',
  forgetful: '이름·원본·기억을 지우는 표현을 보존하는 관계로 바꿔 보세요.',
}

interface MetaphorChange {
  readonly before: string
  readonly after: string
}

function changedExpression(before: string, after: string): MetaphorChange | null {
  if (before === after) return null
  const beforeWords = before.split(' ')
  const afterWords = after.split(' ')
  let prefix = 0
  while (
    prefix < beforeWords.length &&
    prefix < afterWords.length &&
    beforeWords[prefix] === afterWords[prefix]
  ) {
    prefix += 1
  }

  let suffix = 0
  while (
    suffix < beforeWords.length - prefix &&
    suffix < afterWords.length - prefix &&
    beforeWords[beforeWords.length - 1 - suffix] === afterWords[afterWords.length - 1 - suffix]
  ) {
    suffix += 1
  }

  const beforeEnd = suffix === 0 ? beforeWords.length : beforeWords.length - suffix
  const afterEnd = suffix === 0 ? afterWords.length : afterWords.length - suffix
  const clipped = (value: string): string => {
    const points = Array.from(value || '∅')
    return points.length <= 42 ? points.join('') : `${points.slice(0, 41).join('')}…`
  }

  return {
    before: clipped(beforeWords.slice(prefix, beforeEnd).join(' ')),
    after: clipped(afterWords.slice(prefix, afterEnd).join(' ')),
  }
}

function escapeHtml(value: string): string {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;')
}

function traitLabel(trait: Trait | string): string {
  return TRAIT_LABELS[trait as Trait] ?? trait
}

function appendTracedSentence(
  container: HTMLElement,
  sentence: string,
  traces: readonly DisplayExcerptAnchor[],
): void {
  const boundaries = [...new Set([
    0,
    sentence.length,
    ...traces.flatMap(({ start, end }) => [start, end]),
  ])].sort((left, right) => left - right)

  for (let index = 0; index < boundaries.length - 1; index += 1) {
    const start = boundaries[index]
    const end = boundaries[index + 1]
    if (end <= start) continue
    const text = sentence.slice(start, end)
    const activeTraits = traces
      .filter((trace) => trace.start <= start && trace.end >= end)
      .map(({ trait }) => trait)
    if (activeTraits.length === 0) {
      container.append(document.createTextNode(text))
      continue
    }

    const mark = document.createElement('mark')
    mark.dataset.testid = 'source-highlight'
    mark.dataset.traits = activeTraits.join(',')
    mark.textContent = text
    container.append(mark)
  }
}

function hashText(value: string): string {
  let hash = 0x811c9dc5
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index)
    hash = Math.imul(hash, 0x01000193)
  }
  return (hash >>> 0).toString(16).padStart(8, '0')
}

function sleep(milliseconds: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, milliseconds))
}

export interface MetaphorgeDebugApi {
  getState(): GameState
  getDigest(): string
  restart(): void
}

export class GameController {
  private readonly root: HTMLElement
  private state = createInitialGameState(INITIAL_SELECTION)
  private customMetaphor: string | null = null
  private busy = false
  private requestSerial = 0
  private submitStartedAt = 0
  private activeRequest: AbortController | null = null
  private readonly fastMode: boolean
  private readonly reducedMotion: boolean
  private readonly interpreter: SemanticInterpreter

  constructor(
    root: HTMLElement,
    interpreter: SemanticInterpreter = createSemanticInterpreter(),
  ) {
    this.root = root
    this.interpreter = interpreter
    this.fastMode = new URLSearchParams(window.location.search).get('testMode') === '1'
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
  }

  start(): void {
    this.mountShell()
    createGame('phaser-stage')
    this.render()
    const scenario = getCurrentScenario(this.state)
    if (scenario !== null) {
      emitFx({ type: 'stage', anomaly: scenario.kind, index: this.state.roundIndex })
    }
    if (this.fastMode) {
      this.root.dataset.onboarding = 'skipped'
    } else {
      this.root.dataset.onboarding = 'active'
      showOnboarding(() => {
        this.root.dataset.onboarding = 'complete'
        this.announce('문장을 열쇠로 벼려 자물쇠의 빈 홈을 채우세요.')
      })
    }
  }

  getDebugApi(): MetaphorgeDebugApi {
    return {
      getState: () => structuredClone(this.state),
      getDigest: () => canonicalStateDigest(this.state),
      restart: () => this.restart(),
    }
  }

  private mountShell(): void {
    this.root.innerHTML = `
      <div class="game-layout">
        <section class="stage-panel" aria-label="관측 대상 화면">
          <div class="stage-brand" aria-hidden="true">
            <div class="stage-mark"><span>MF</span></div>
            <div>
              <p class="stage-title">METAPHORGE</p>
              <p class="stage-subtitle">은유로 이상 현상을 멈추는 격리실</p>
            </div>
          </div>
          <div id="phaser-stage"></div>
          <div class="stage-foot">
            <p>공격은 현상을 키웁니다. 물체가 따를 비유를 한 문장으로 만드세요.</p>
            <span class="adapter-badge">의미 해석기 · 오프라인</span>
          </div>
        </section>
        <main class="forge-panel" id="game-ui"></main>
      </div>
      <div class="sr-only" id="game-announcer" aria-live="polite"></div>
    `
  }

  private render(): void {
    const scenario = getCurrentScenario(this.state)
    if (scenario === null) return

    const gameUi = this.requiredElement<HTMLElement>('#game-ui')
    gameUi.classList.remove('is-presenting-result')
    const complete = isSelectionComplete(scenario, this.state.selection)
    const metaphor = this.customMetaphor ?? composeMetaphor(scenario, this.state.selection)
    const slotsForDisplay = [...scenario.slots].sort((left, right) => {
      const leftSelected = this.state.selection[left.id] === undefined ? 0 : 1
      const rightSelected = this.state.selection[right.id] === undefined ? 0 : 1
      return leftSelected - rightSelected
    })
    const inherited = scenario.inheritance === 'all-previous-success-traits'
      ? [...new Set(this.state.successes.flatMap(({ effectiveTraits }) => effectiveTraits))]
      : []
    const inheritedRequired = scenario.requiredTraits.filter((trait) => inherited.includes(trait))
    const stickyKeyways = scenario.requiredTraits
      .map((trait) => `<span
        class="sticky-keyway${inherited.includes(trait) ? ' is-memory' : ''}"
        data-trait="${trait}"
        data-filled="${inherited.includes(trait) ? 'memory' : 'empty'}"
      ></span>`)
      .join('')

    const requiredTraits = scenario.requiredTraits
      .map((trait) => {
        const inheritedTrait = inherited.includes(trait)
        return `<span
          class="keyway-slot${inheritedTrait ? ' is-memory-filled' : ''}"
          data-trait="${trait}"
          data-testid="keyway-${trait}"
          data-filled="${inheritedTrait ? 'memory' : 'empty'}"
        >
          <span class="keyway-cut" aria-hidden="true"></span>
          <span class="keyway-copy">${escapeHtml(TRAIT_CLUES[trait])}</span>
          ${inheritedTrait ? '<span class="memory-seal">기억</span>' : '<span class="keyway-empty">빈 홈</span>'}
        </span>`
      })
      .join('')
    const forbiddenTraits = scenario.forbiddenTraits
      .map((trait) => `<span class="thorn-trait" data-trait="${trait}">
        <span class="thorn-mark" aria-hidden="true"></span>
        ${escapeHtml(TRAIT_CLUES[trait])}
      </span>`)
      .join('')
    const scenarioAttempts = this.state.attempts.filter(
      ({ evaluation }) => evaluation.scenarioId === scenario.id,
    )
    const firstGuide = scenario.number === 1 && scenarioAttempts.length === 0
    const repairing =
      this.state.status === 'playing' &&
      scenarioAttempts.length === 1 &&
      scenarioAttempts.at(-1)?.evaluation.stable === false
    const previousSentence = scenarioAttempts.at(-1)?.evaluation.sentence ?? ''
    const editedOnRender = repairing &&
      normalizeMetaphorInput(metaphor) !== previousSentence
    const actionMarkup = `
      <button
        class="forge-action"
        type="button"
        data-testid="submit-metaphor"
        ${complete && !this.busy ? '' : 'disabled'}
      >${firstGuide && complete ? '2/2 · 열쇠로 벼리기' : '문장을 열쇠로 벼리기'}</button>
    `
    const slotMarkup = slotsForDisplay.map((slot) => {
      const selectedChipId = this.state.selection[slot.id]
      const slotState = selectedChipId === undefined ? ' is-focus' : ' is-filled'
      return `
        <fieldset class="metaphor-slot${slotState}">
          <legend class="sr-only">${escapeHtml(slot.label)}</legend>
          <div class="slot-head" aria-hidden="true">
            <span class="slot-label">${escapeHtml(slot.label)}</span>
            <span class="slot-prompt">${escapeHtml(slot.prompt)}</span>
          </div>
          <div class="chip-row">
            ${slot.chips.map((chip) => {
              const selected = selectedChipId === chip.id
              return `
                <button
                  class="phrase-chip"
                  type="button"
                  aria-pressed="${String(selected)}"
                  data-slot-id="${escapeHtml(slot.id)}"
                  data-chip-id="${escapeHtml(chip.id)}"
                  data-testid="chip-${escapeHtml(chip.id)}"
                  ${this.busy ? 'disabled' : ''}
                >${escapeHtml(chip.text)}</button>
              `
            }).join('')}
          </div>
        </fieldset>
      `
    }).join('')
    const guideMarkup = firstGuide
      ? `<aside class="guide-callout" data-testid="first-guide">
          <strong>${complete ? '안내 2/2' : '안내 1/2'}</strong>
          ${complete
            ? '완성 문장을 읽고 바로 적용하세요. 직접 편집은 다음 시도부터 써도 됩니다.'
            : '이미 두 조각을 채웠습니다. 빛나는 ‘닻’ 하나만 고르세요.'}
        </aside>`
      : ''
    const repairMarkup = repairing
      ? `<aside class="edit-nudge" id="edit-nudge" data-testid="edit-nudge">
          <strong>이번에는 칩보다 문장을 고쳐 보세요.</strong>
          결과에서 부족하거나 충돌한 뜻을 확인했습니다. 아래 표현 하나를 직접 바꾸면 해석기가 차이를 짚어 줍니다.
        </aside>`
      : ''
    gameUi.innerHTML = `
      <header class="run-header">
        <div>
          <p class="eyebrow">${escapeHtml(scenario.code.split(' / ')[0])} · ${scenario.number}차 / 3차</p>
          <h1 class="anomaly-title">${escapeHtml(scenario.name)}</h1>
        </div>
        <div class="integrity" aria-label="격리실 내구도 ${this.state.integrity}/2">
          <span class="integrity-label">격리실 내구도</span>
          <span class="integrity-pips" aria-hidden="true">
            ${[0, 1].map((index) => `<span class="integrity-pip${index < this.state.integrity ? ' is-live' : ''}"></span>`).join('')}
          </span>
        </div>
      </header>

      <p class="target-line" data-testid="round-goal">
        <strong>이 자물쇠를 여는 문장</strong><small>격리 목표</small>
        <span class="target-copy">${escapeHtml(GOAL_LINES[scenario.id] ?? scenario.question)}</span>
        <span
          class="sticky-lock-summary"
          data-testid="sticky-lock-summary"
          data-required-count="${scenario.requiredTraits.length}"
          data-memory-count="${inheritedRequired.length}"
          data-forbidden-count="${scenario.forbiddenTraits.length}"
          aria-label="기억으로 채운 홈 ${inheritedRequired.length}개, 빈 홈 ${scenario.requiredTraits.length - inheritedRequired.length}개, 금지 가시 ${scenario.forbiddenTraits.length}개"
        >
          <span class="sticky-lock-label">홈</span>
          <span class="sticky-keyways" aria-hidden="true">${stickyKeyways}</span>
          <span class="sticky-thorn-count">가시 ${scenario.forbiddenTraits.length}</span>
        </span>
      </p>

      <section class="brief" aria-labelledby="scenario-question">
        <p class="brief-copy">${escapeHtml(scenario.brief)}</p>
        <p class="sr-only" id="scenario-question">${escapeHtml(scenario.question)}</p>
      </section>

      ${inherited.length > 0 ? `
        <aside class="twist-banner" data-testid="twist">
          <strong>거울은 당신이 이미 말한 것을 기억합니다.</strong>
          <span>이전 문장이 ${inheritedRequired.length}개의 홈을 미리 채웠습니다.</span>
        </aside>
      ` : ''}

      <section class="lock-map" data-testid="trait-lock" aria-label="이 존재의 성질 자물쇠">
        <section class="lock-side lock-required" data-testid="required-keyways">
          <div class="lock-heading">
            <span class="lock-function">이 존재가 원하는 것</span>
            <small>격리 신호</small>
          </div>
          <div class="keyway-list">${requiredTraits}</div>
        </section>
        <div class="lock-heart" aria-hidden="true">
          <span class="lock-heart-ring"></span>
          <span class="lock-heart-keyhole"></span>
        </div>
        <section class="lock-side lock-forbidden" data-testid="forbidden-thorns">
          <div class="lock-heading">
            <span class="lock-function">이 존재가 거부하는 것</span>
            <small>파열 조짐</small>
          </div>
          <div class="thorn-list">${forbiddenTraits}</div>
        </section>
        <p class="lock-caption">문장에서 뽑힌 성질로 빈 홈을 채우고, 붉은 가시는 피하세요.</p>
      </section>

      ${guideMarkup}
      ${repairMarkup}

      <div class="editor-head${complete ? '' : ' is-incomplete'}">
        <label for="metaphor-input">${repairing ? '직접 고칠 열쇠 문장' : '당신의 열쇠 문장'} <small>${repairing ? '문장 편집' : '완성 문장'}</small></label>
        <span>${complete
          ? repairing
            ? '표현을 바꾸면 다음 결과가 바뀐 부분을 표시합니다.'
            : '칩은 시작점입니다. 문장을 직접 고쳐도 됩니다.'
          : '세 조각을 고른 뒤 문장을 직접 다듬을 수 있습니다.'}</span>
        ${repairing ? `<span class="edit-status" data-testid="edit-status">${editedOnRender ? '문장이 수정되었습니다.' : '아직 같은 문장입니다.'}</span>` : ''}
      </div>
      <div class="metaphor-preview${complete ? '' : ' is-incomplete'}${editedOnRender ? ' is-edited' : ''}" data-testid="metaphor-preview">
        <textarea
          class="metaphor-input"
          id="metaphor-input"
          data-testid="metaphor-input"
          maxlength="180"
          rows="3"
          ${repairing ? 'aria-describedby="edit-nudge"' : ''}
          ${this.busy || !complete ? 'disabled' : ''}
        >${escapeHtml(metaphor)}</textarea>
      </div>

      ${complete ? actionMarkup : ''}

      <div class="slot-list">
        ${slotMarkup}
      </div>

      ${complete ? '' : actionMarkup}

      <section class="scan-pipeline" data-testid="scan-pipeline" aria-live="polite">
        <p class="scan-status">${complete
          ? '문장을 벼리면 성질 추출 → 홈 대조 → 판정을 차례로 보여 줍니다.'
          : '세 조각을 골라 한 문장을 완성하세요.'}</p>
        <div class="scan-track" aria-hidden="true"><div class="scan-progress"></div></div>
      </section>

      <section class="result-panel" data-testid="result" aria-live="polite"></section>

      <nav class="round-progress" aria-label="격리 진행도">
        ${[0, 1, 2].map((index) => {
          const status = index < this.state.successes.length
            ? ' is-complete'
            : index === this.state.roundIndex
              ? ' is-current'
              : ''
          return `<span class="round-node${status}">${index + 1}차 ${index === 2 ? '거울' : '관측'}</span>`
        }).join('')}
      </nav>
    `

    for (const button of gameUi.querySelectorAll<HTMLButtonElement>('[data-chip-id]')) {
      button.addEventListener('click', () => {
        if (this.busy) return
        const slotId = button.dataset.slotId
        const chipId = button.dataset.chipId
        if (slotId === undefined || chipId === undefined) return
        this.state = gameReducer(this.state, { type: 'SELECT_CHIP', slotId, chipId })
        this.customMetaphor = null
        this.render()
      })
    }

    gameUi
      .querySelector<HTMLTextAreaElement>('[data-testid="metaphor-input"]')
      ?.addEventListener('input', (event) => {
        this.customMetaphor = (event.currentTarget as HTMLTextAreaElement).value
        if (repairing) {
          const edited = normalizeMetaphorInput(this.customMetaphor) !== previousSentence
          gameUi.querySelector<HTMLElement>('[data-testid="metaphor-preview"]')
            ?.classList.toggle('is-edited', edited)
          const status = gameUi.querySelector<HTMLElement>('[data-testid="edit-status"]')
          if (status !== null) {
            status.textContent = edited
              ? '문장이 수정되었습니다.'
              : '아직 같은 문장입니다.'
          }
        }
      })

    gameUi
      .querySelector<HTMLButtonElement>('[data-testid="submit-metaphor"]')
      ?.addEventListener('click', () => void this.submit(scenario))

    this.syncDebugState()
  }

  private async submit(scenario: AnomalyScenario): Promise<void> {
    if (this.busy || !isSelectionComplete(scenario, this.state.selection)) return

    this.busy = true
    this.submitStartedAt = performance.now()
    this.setInputsDisabled(true)
    this.requestSerial += 1
    const serial = this.requestSerial
    this.activeRequest?.abort()
    this.activeRequest = new AbortController()
    emitFx({ type: 'scan', traits: [] })

    const request = this.interpretationRequest(scenario)
    const interpretationPromise = this.interpreter.interpret(
      request,
      this.activeRequest.signal,
    )
    const pipelinePromise = this.runScanPipeline()
    this.root.dataset.acknowledgementMs = (
      performance.now() - this.submitStartedAt
    ).toFixed(2)

    try {
      const [interpretation] = await Promise.all([
        interpretationPromise,
        pipelinePromise,
      ])
      if (serial !== this.requestSerial) return

      const nextState = gameReducer(this.state, {
        type: 'RESOLVE_INTERPRETATION',
        traits: canonicalizeTraits(interpretation.canonicalTraits),
        metaphor: request.metaphor,
      })
      const evaluation = nextState.lastEvaluation
      if (evaluation === null || nextState.attempts.length === this.state.attempts.length) {
        throw new Error('Meaning interpretation did not resolve a complete metaphor')
      }

      this.state = nextState
      await this.showResult(scenario, evaluation, interpretation, serial)
    } catch (error) {
      if (serial !== this.requestSerial) return
      this.busy = false
      this.setInputsDisabled(false)
      this.setScanStatus('해석기가 응답하지 않았습니다. 문장을 다시 적용하세요.', 0)
      console.error(error)
    }
  }

  private interpretationRequest(
    scenario: AnomalyScenario,
  ): InterpretationRequest {
    const metaphor = normalizeMetaphorInput(
      this.customMetaphor ?? composeMetaphor(scenario, this.state.selection)
    )
    const selectedFragments = scenario.slots.flatMap((slot) => {
      const chipId = this.state.selection[slot.id]
      const chip = slot.chips.find(({ id }) => id === chipId)
      return chip === undefined ? [] : [{ id: chip.id, text: chip.text }]
    })
    const inheritedTraits = deriveInheritedTraits(scenario, this.state.successes)
    const requestHash = hashText([
      scenario.id,
      metaphor.normalize('NFKC'),
      selectedFragments.map(({ id }) => id).join('|'),
      inheritedTraits.join('|'),
    ].join('::'))

    return {
      schemaVersion: 1,
      requestId: `${scenario.id}-${requestHash}`,
      anomalyId: scenario.id,
      anomalyName: scenario.name,
      metaphor,
      selectedFragments,
      traitVocabulary: TRAITS,
      inheritedTraits,
    }
  }

  private async runScanPipeline(): Promise<void> {
    const delays = this.fastMode ? [8, 8, 8, 12] : [420, 650, 700, 800]
    const progresses = [8, 32, 64, 88]

    this.requiredElement<HTMLElement>('[data-testid="scan-pipeline"]').classList.add('is-active')
    for (let index = 0; index < SCAN_STEPS.length; index += 1) {
      this.setScanStatus(SCAN_STEPS[index], progresses[index])
      await sleep(delays[index])
    }
    this.setScanStatus('해석 완료 · 물리 결과를 관측합니다.', 100)
  }

  private setScanStatus(message: string, progress: number): void {
    const status = this.root.querySelector<HTMLElement>('.scan-status')
    const bar = this.root.querySelector<HTMLElement>('.scan-progress')
    if (status !== null) status.textContent = message
    if (bar !== null) bar.style.width = `${progress}%`
    const announcer = this.root.querySelector<HTMLElement>('#game-announcer')
    if (announcer !== null && progress === 8) announcer.textContent = message
  }

  private async showResult(
    scenario: AnomalyScenario,
    evaluation: MetaphorEvaluation,
    interpretation: Interpretation,
    serial: number,
  ): Promise<void> {
    this.root.dataset.revealMs = (performance.now() - this.submitStartedAt).toFixed(2)
    const gameUi = this.requiredElement<HTMLElement>('#game-ui')
    gameUi.classList.add('is-presenting-result')
    const panel = this.requiredElement<HTMLElement>('[data-testid="result"]')
    const terminal = this.state.status !== 'playing'
    panel.className = 'result-panel is-open'
    panel.dataset.revealOrder = 'traits,comparison,verdict'
    panel.innerHTML = `
      <section class="result-step is-visible" data-result-step="traits" data-testid="result-traits" tabindex="-1">
        <p class="step-kicker">1 / 3 · 문장을 녹여 성질을 뽑습니다</p>
        <p class="manifestation"></p>
        <div class="edit-impact" data-testid="edit-impact" hidden></div>
        <figure class="meaning-trace" data-testid="meaning-trace">
          <blockquote class="trace-sentence" data-testid="trace-sentence"></blockquote>
          <figcaption>빛난 구절들이 함께 녹아 성질 카드가 됩니다.</figcaption>
          <div class="reading-list" data-testid="trait-rack"></div>
        </figure>
        <button class="result-stage-next" type="button" data-testid="reveal-comparison">열쇠날을 자물쇠에 맞대기</button>
      </section>
      <section class="result-step" data-result-step="comparison" data-testid="result-comparison" tabindex="-1">
        <p class="step-kicker">2 / 3 · 열쇠와 자물쇠를 맞댑니다</p>
        <div class="comparison-grid"></div>
        <p class="revision-hint" data-testid="revision-hint"></p>
        <button class="result-stage-next" type="button" data-testid="reveal-verdict">최종 판정 확인</button>
      </section>
      <section class="result-step result-verdict" data-result-step="verdict" data-testid="result-verdict" tabindex="-1">
        <p class="result-kicker"></p>
        <h2 class="result-title"></h2>
        <p class="physical-outcome"></p>
        <p class="diagnostic"></p>
        ${terminal ? this.recapMarkup() : ''}
        <button class="result-continue" type="button" data-testid="continue"></button>
      </section>
    `

    const title = this.state.status === 'won'
      ? '격리 완료 · 의미가 주인을 놓았습니다.'
      : this.state.status === 'lost'
        ? '격리실 파열 · 문장이 현상을 삼켰습니다.'
        : evaluation.stable
          ? '안정화 · 은유가 물리 법칙이 되었습니다.'
          : '파열 · 예상 밖의 성질이 남았습니다.'
    const buttonLabel = terminal
      ? '처음부터 다시 시작'
      : evaluation.stable
        ? '다음 물체로 이동'
        : '문장을 직접 고치기'

    this.requiredElement<HTMLElement>('.result-kicker', panel).textContent = evaluation.stable
      ? '3 / 3 · 판정 · 안정'
      : '3 / 3 · 판정 · 파열'
    this.requiredElement<HTMLElement>('.result-title', panel).textContent = title
    const physicalOutcome = evaluation.stable ? scenario.stableCopy : scenario.unstableCopy
    this.requiredElement<HTMLElement>('.manifestation', panel).textContent = interpretation.manifestation
    this.requiredElement<HTMLElement>('.physical-outcome', panel).textContent = physicalOutcome

    const traces = anchorConfirmedTraits(
      evaluation.sentence,
      evaluation.interpretedTraits,
    )
    const traceByTrait = new Map(traces.map((trace) => [trace.trait, trace]))
    const tracedSentence = this.requiredElement<HTMLElement>('[data-testid="trace-sentence"]', panel)
    tracedSentence.setAttribute('aria-label', evaluation.sentence)
    appendTracedSentence(tracedSentence, evaluation.sentence, traces)

    const readings = this.requiredElement<HTMLElement>('.reading-list', panel)
    if (interpretation.readings.length === 0) {
      const empty = document.createElement('p')
      empty.className = 'empty-reading'
      empty.textContent = '해석기가 붙잡을 관계를 찾지 못했습니다. 단어보다 문장의 움직임과 결말을 바꿔 보세요.'
      readings.append(empty)
    }
    for (const reading of interpretation.readings) {
      const trace = traceByTrait.get(reading.trait as Trait)
      const item = document.createElement('div')
      item.className = 'trait-reading is-traced'
      item.dataset.testid = 'trait-extraction'
      item.dataset.trait = reading.trait
      const source = document.createElement('span')
      source.className = 'trace-source-fragment'
      source.dataset.testid = 'trace-source'
      source.dataset.trait = reading.trait
      source.textContent = `“${trace?.excerpt ?? evaluation.sentence}”`
      const sourceBasis = document.createElement('small')
      sourceBasis.textContent = trace?.basis === 'clause'
        ? '표현의 관계가 읽힘'
        : '문장 전체가 함께 읽힘'
      source.append(sourceBasis)
      const connector = document.createElement('span')
      connector.className = 'trace-connector'
      connector.setAttribute('aria-hidden', 'true')
      connector.innerHTML = '<span></span>'
      const label = document.createElement('strong')
      label.className = 'trait-card'
      label.dataset.testid = 'trace-trait-card'
      label.dataset.trait = reading.trait
      label.textContent = traitLabel(reading.trait)
      const evidence = document.createElement('span')
      evidence.className = 'trace-evidence'
      evidence.textContent = reading.evidence
      item.append(source, connector, label, evidence)
      readings.append(item)
    }

    const sameScenarioAttempts = this.state.attempts.filter(
      ({ evaluation: attempt }) => attempt.scenarioId === scenario.id,
    )
    const previousEvaluation = sameScenarioAttempts.at(-2)?.evaluation
    const change = previousEvaluation === undefined
      ? null
      : changedExpression(previousEvaluation.sentence, evaluation.sentence)
    if (change !== null && previousEvaluation !== undefined) {
      const addedTraits = evaluation.interpretedTraits.filter(
        (trait) => !previousEvaluation.interpretedTraits.includes(trait),
      )
      const removedTraits = previousEvaluation.interpretedTraits.filter(
        (trait) => !evaluation.interpretedTraits.includes(trait),
      )
      const impact = this.requiredElement<HTMLElement>('[data-testid="edit-impact"]', panel)
      impact.hidden = false
      impact.dataset.addedTraits = addedTraits.join(',')
      impact.dataset.removedTraits = removedTraits.join(',')
      const prefix = document.createTextNode(`바꾼 표현: “${change.before}” → `)
      const mark = document.createElement('mark')
      mark.textContent = change.after
      const traitChanges = [
        addedTraits.length > 0
          ? `${addedTraits.map(traitLabel).join(' · ')} 성질을 새로 불렀습니다.`
          : '',
        removedTraits.length > 0
          ? `${removedTraits.map(traitLabel).join(' · ')} 성질은 사라졌습니다.`
          : '',
      ].filter((part) => part.length > 0)
      const suffix = document.createTextNode(traitChanges.length > 0
        ? `. 이 변화가 ${traitChanges.join(' ')}`
        : '. 표현은 달라졌지만 제한된 성질 조합은 같게 읽혔습니다.')
      impact.append(prefix, mark, suffix)
    }

    const comparison = this.requiredElement<HTMLElement>('.comparison-grid', panel)
    comparison.classList.add('lock-comparison')
    const extractedRail = document.createElement('section')
    extractedRail.className = 'extracted-key'
    extractedRail.dataset.testid = 'extracted-key'
    const extractedLabel = document.createElement('p')
    extractedLabel.textContent = '이번 문장에서 깎인 열쇠날'
    const extractedTokens = document.createElement('div')
    extractedTokens.className = 'extracted-token-list'
    for (const trait of evaluation.interpretedTraits) {
      const required = evaluation.requiredTraits.includes(trait)
      const forbiddenTrait = evaluation.forbiddenTraits.includes(trait)
      const motion = required ? 'plug' : forbiddenTrait ? 'thorn' : 'bounce'
      const token = document.createElement('span')
      token.className = `extracted-token is-${motion}`
      token.dataset.trait = trait
      token.dataset.lockMotion = motion
      token.dataset.testid = 'extracted-token'
      token.textContent = traitLabel(trait)
      if (motion === 'bounce') {
        const staticState = document.createElement('small')
        staticState.className = 'extracted-token-state'
        staticState.textContent = '맞는 홈 없음'
        token.append(staticState)
      }
      extractedTokens.append(token)
    }
    if (evaluation.interpretedTraits.length === 0) {
      const emptyToken = document.createElement('span')
      emptyToken.className = 'extracted-token is-empty'
      emptyToken.textContent = '깎인 날 없음'
      extractedTokens.append(emptyToken)
    }
    extractedRail.append(extractedLabel, extractedTokens)

    const lockPlate = document.createElement('div')
    lockPlate.className = 'comparison-lockplate'
    lockPlate.dataset.testid = 'comparison-lockplate'
    const requiredBlock = document.createElement('section')
    requiredBlock.className = 'comparison-keyways'
    const requiredHeading = document.createElement('strong')
    requiredHeading.textContent = '빈 홈에 맞은 성질'
    requiredBlock.append(requiredHeading)
    for (const trait of evaluation.requiredTraits) {
      const interpreted = evaluation.interpretedTraits.includes(trait)
      const inheritedTrait = evaluation.inheritedTraits.includes(trait)
      const state = inheritedTrait ? 'inherited' : interpreted ? 'matched' : 'missing'
      const row = document.createElement('div')
      row.className = `comparison-keyway is-${state}`
      row.dataset.trait = trait
      row.dataset.state = state
      row.dataset.testid = 'comparison-keyway'
      const cut = document.createElement('span')
      cut.className = 'comparison-keyway-cut'
      cut.setAttribute('aria-hidden', 'true')
      const copy = document.createElement('span')
      copy.className = 'comparison-keyway-copy'
      copy.textContent = TRAIT_CLUES[trait]
      const status = document.createElement('small')
      status.textContent = inheritedTrait
        ? `${traitLabel(trait)} · 이전 문장`
        : interpreted
          ? `${traitLabel(trait)} · 맞음`
          : `${traitLabel(trait)} · 비어 있음`
      row.append(cut, copy, status)
      requiredBlock.append(row)
    }

    const forbiddenBlock = document.createElement('section')
    forbiddenBlock.className = 'comparison-thorns'
    const forbiddenHeading = document.createElement('strong')
    forbiddenHeading.textContent = '거부 가시와의 접촉'
    forbiddenBlock.append(forbiddenHeading)
    for (const trait of evaluation.forbiddenTraits) {
      const triggered = evaluation.effectiveTraits.includes(trait)
      const row = document.createElement('span')
      row.className = `comparison-thorn ${triggered ? 'is-hit' : 'is-clear'}`
      row.dataset.trait = trait
      row.dataset.state = triggered ? 'hit' : 'clear'
      row.dataset.testid = 'comparison-thorn'
      row.textContent = `${triggered ? '접촉' : '안전'} · ${TRAIT_CLUES[trait]}`
      forbiddenBlock.append(row)
    }
    lockPlate.append(requiredBlock, forbiddenBlock)
    comparison.append(extractedRail, lockPlate)

    const missing = evaluation.missingRequiredTraits.map(traitLabel)
    const forbidden = evaluation.triggeredForbiddenTraits.map(traitLabel)
    const revisionHint = this.requiredElement<HTMLElement>('.revision-hint', panel)
    const collisionEvidence = interpretation.readings
      .filter(({ trait }) => evaluation.triggeredForbiddenTraits.includes(trait as Trait))
      .map(({ evidence }) => evidence)
    const repairDirections = [
      ...evaluation.triggeredForbiddenTraits.map((trait) => REPAIR_HINTS[trait]),
      ...evaluation.missingRequiredTraits.map((trait) => REPAIR_HINTS[trait]),
    ]
    revisionHint.textContent = evaluation.stable
      ? '필요한 관계가 모두 채워졌고, 피해야 할 뜻은 남지 않았습니다.'
      : [
          ...collisionEvidence.map((evidence) => `현재 표현은 이렇게 읽혔습니다: ${evidence}`),
          ...repairDirections,
        ].join(' ')

    const diagnosticParts = evaluation.stable
      ? ['문장의 뜻이 목표를 통과했습니다.', interpretation.afterimage]
      : [
          missing.length > 0 ? `아직 비어 있음: ${missing.join(' · ')}.` : '',
          forbidden.length > 0 ? `걷어낼 충돌: ${forbidden.join(' · ')}.` : '',
          interpretation.afterimage,
        ].filter((value) => value.length > 0)
    this.requiredElement<HTMLElement>('.diagnostic', panel).textContent = diagnosticParts.join(' ')

    const continueButton = this.requiredElement<HTMLButtonElement>('[data-testid="continue"]', panel)
    continueButton.textContent = buttonLabel
    continueButton.addEventListener('click', () => {
      if (terminal) {
        this.restart()
        return
      }

      this.busy = false
      if (evaluation.stable) this.customMetaphor = null
      this.render()
      const nextScenario = getCurrentScenario(this.state)
      if (nextScenario !== null) {
        emitFx({ type: 'stage', anomaly: nextScenario.kind, index: this.state.roundIndex })
      }
      if (evaluation.stable) {
        this.root.querySelector<HTMLElement>('.run-header')?.scrollIntoView({
          behavior: this.scrollBehavior(),
          block: 'start',
        })
      } else {
        const editor = this.root.querySelector<HTMLTextAreaElement>('[data-testid="metaphor-input"]')
        editor?.focus({ preventScroll: true })
        editor?.setSelectionRange(editor.value.length, editor.value.length)
        editor?.scrollIntoView({ behavior: this.scrollBehavior(), block: 'center' })
      }
    })

    this.root.dataset.resultStage = 'traits'
    const traitsStep = this.requiredElement<HTMLElement>('[data-result-step="traits"]', panel)
    traitsStep.focus({ preventScroll: true })
    panel.scrollIntoView({ behavior: this.scrollBehavior(), block: 'start' })
    await this.waitForResultAdvance('[data-testid="reveal-comparison"]')
    if (serial !== this.requestSerial) return
    this.requiredElement<HTMLElement>('[data-result-step="traits"]', panel).classList.remove('is-visible')
    const comparisonStep = this.requiredElement<HTMLElement>('[data-result-step="comparison"]', panel)
    comparisonStep.classList.add('is-visible')
    this.root.dataset.resultStage = 'comparison'
    comparisonStep.focus({ preventScroll: true })
    comparisonStep.scrollIntoView({
      behavior: this.scrollBehavior(),
      block: 'start',
    })
    await this.waitForResultAdvance('[data-testid="reveal-verdict"]')
    if (serial !== this.requestSerial) return
    this.requiredElement<HTMLElement>('[data-result-step="comparison"]', panel).classList.remove('is-visible')
    const verdictStep = this.requiredElement<HTMLElement>('[data-result-step="verdict"]', panel)
    verdictStep.classList.add('is-visible')
    panel.classList.add(evaluation.stable ? 'is-stable' : 'is-breach')
    panel.dataset.verdict = evaluation.stable ? 'stable' : 'breach'
    this.root.dataset.resultStage = 'verdict'
    this.root.dataset.verdictMs = (performance.now() - this.submitStartedAt).toFixed(2)
    this.syncDebugState()
    this.announce(`${evaluation.stable ? '안정' : '파열'} 판정입니다. 결과와 다음 행동을 확인하세요.`)
    const integrity = this.root.querySelector<HTMLElement>('.integrity')
    integrity?.setAttribute('aria-label', `격리실 내구도 ${this.state.integrity}/2`)
    for (const [index, pip] of [
      ...this.root.querySelectorAll<HTMLElement>('.integrity-pip'),
    ].entries()) {
      pip.classList.toggle('is-live', index < this.state.integrity)
    }
    emitFx({
      type: evaluation.stable ? 'stable' : 'breach',
      traits: evaluation.effectiveTraits.map(traitLabel),
    })
    this.busy = false
    verdictStep.focus({ preventScroll: true })
    verdictStep.scrollIntoView({
      behavior: this.scrollBehavior(),
      block: 'start',
    })
  }

  private recapMarkup(): string {
    const spokenCount = new Set(
      this.state.attempts.map(({ evaluation }) => evaluation.scenarioId),
    ).size
    const rounds = SCENARIOS.map((scenario) => {
      const finalEvaluation = this.state.attempts
        .filter(({ evaluation }) => evaluation.scenarioId === scenario.id)
        .at(-1)?.evaluation
      if (finalEvaluation === undefined) {
        return `<li class="recap-round is-unreached" data-testid="recap-round" data-round="${scenario.number}">
          <div class="recap-round-head"><span>0${scenario.number}</span>${escapeHtml(scenario.name)}</div>
          <blockquote data-testid="recap-sentence">아직 말하지 못한 문장</blockquote>
          <div class="recap-traits is-empty" data-testid="recap-traits">봉인된 기록</div>
        </li>`
      }

      const traits = finalEvaluation.interpretedTraits.length > 0
        ? finalEvaluation.interpretedTraits
          .map((trait) => `<span>${escapeHtml(traitLabel(trait))}</span>`)
          .join('')
        : '<span class="is-empty">뽑힌 성질 없음</span>'
      return `<li class="recap-round${finalEvaluation.stable ? ' is-stable' : ' is-breach'}" data-testid="recap-round" data-round="${scenario.number}">
        <div class="recap-round-head"><span>0${scenario.number}</span>${escapeHtml(scenario.name)}</div>
        <blockquote data-testid="recap-sentence">${escapeHtml(finalEvaluation.sentence)}</blockquote>
        <div class="recap-traits" data-testid="recap-traits">${traits}</div>
      </li>`
    }).join('')

    return `<section class="run-recap" data-testid="run-recap" aria-label="당신이 이 존재들에게 준 세 문장">
      <header class="recap-header">
        <small>METAPHORGE · 격리 기록</small>
        <h3>당신이 이 존재들에게 준 세 문장</h3>
      </header>
      <ol>${rounds}</ol>
      <footer>${this.state.status === 'won' ? '격리 완료' : '격리 중단'} · ${spokenCount}개의 문장이 기록됨</footer>
    </section>`
  }

  private announce(message: string): void {
    const announcer = this.root.querySelector<HTMLElement>('#game-announcer')
    if (announcer !== null) announcer.textContent = message
  }

  private waitForResultAdvance(selector: string): Promise<void> {
    const button = this.requiredElement<HTMLButtonElement>(selector)
    return new Promise((resolve) => {
      button.addEventListener('click', () => resolve(), { once: true })
    })
  }

  private scrollBehavior(): ScrollBehavior {
    return this.fastMode || this.reducedMotion ? 'auto' : 'smooth'
  }

  private restart(): void {
    this.requestSerial += 1
    this.activeRequest?.abort()
    this.activeRequest = null
    this.busy = false
    this.state = createInitialGameState(INITIAL_SELECTION)
    this.customMetaphor = null
    emitFx({ type: 'reset' })
    this.render()
    const scenario = getCurrentScenario(this.state)
    if (scenario !== null) {
      emitFx({ type: 'stage', anomaly: scenario.kind, index: 0 })
    }
  }

  private setInputsDisabled(disabled: boolean): void {
    for (const control of this.root.querySelectorAll<
      HTMLButtonElement | HTMLTextAreaElement
    >('.phrase-chip, .forge-action, .metaphor-input')) {
      control.disabled = disabled
    }
  }

  private syncDebugState(): void {
    this.root.dataset.stateDigest = canonicalStateDigest(this.state)
    this.root.dataset.gameStatus = this.state.status
    this.root.dataset.roundIndex = String(this.state.roundIndex)
  }

  private requiredElement<T extends Element>(selector: string, parent: ParentNode = this.root): T {
    const element = parent.querySelector<T>(selector)
    if (element === null) throw new Error(`Required UI element not found: ${selector}`)
    return element
  }
}
